import { useState } from 'react';
import { useLocation } from 'react-router-dom-v5-compat';
import { Link, useMediaQuery } from '@material-ui/core';

import { APP_LOGO_VARIANT, AppLogo } from 'src/components/AppLogo';
import { AuthenticationBanner } from 'src/components/AuthenticationBanner';
import { AuthTypeSeparator } from 'src/components/AuthTypeSeparator';
import OAuthPanel from 'src/components/OAuthPanel';
import PageLayout from 'src/components/PageLayout';
import { TextLink } from 'src/components/TextLink';
import { UnauthenticatedPageContentLayout } from 'src/components/UnauthenticatedPageContentLayout';
import { UnauthenticatedPageErrorLayout } from 'src/components/UnauthenticatedPageErrorLayout';
import { UnauthenticatedPageFooterTextLink } from 'src/components/UnauthenticatedPageFooterTextLink';
import { UnauthenticatedPageLayout } from 'src/components/UnauthenticatedPageLayout';
import { SUPPORT_URL } from 'src/const';
import { GRAPHQL_ERROR_CODES } from 'src/data/graphqlErrorCodes';
import { isTeadsUI } from 'src/i18n';
import { ROUTES } from 'src/routes';
import { useInviteToken } from 'src/services';

import LoginForm from './components/LoginForm';
import { SSOLogin } from './components/SSOLogin';
import { TeadsLogin } from './components/TeadsLogin';
import { useStyles } from './useStyles';
import { useViewModel } from './useViewModel';

const getTitle = (isSSO, isInvited) => {
  if (isSSO) {
    return 'Continue with SSO';
  } else if (isInvited) {
    return 'You’ve been invited to join an Anyword account!';
  } else {
    return 'Welcome Back';
  }
};

const getSubtitle = (isSSO, isInvited) => {
  if (isSSO) {
    return 'Please log in using your SSO credentials';
  } else if (isInvited) {
    return 'Log in your account to join';
  } else {
    return 'Log in to your account';
  }
};

export const SignInPage = () => {
  const {
    loading,
    submitting,
    error,
    errorCode,
    handleSubmit,
    handleOAuthStart,
    handleOAuthSuccess,
    handleOAuthError,
  } = useViewModel();
  const styles = useStyles();

  const { token } = useInviteToken();
  const isInvited = !!token;
  const isUserWithNoCustomers = errorCode === GRAPHQL_ERROR_CODES.user_has_no_customer;
  const isTeads = isTeadsUI();
  const location = useLocation();
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down('sm'));

  const [isSSO, setIsSSO] = useState(false);

  return isUserWithNoCustomers ? (
    <UnauthenticatedPageLayout>
      <UnauthenticatedPageErrorLayout
        title="Insufficient Permissions"
        subtitle="Your user may have been removed <br/> by an account admin."
      />
    </UnauthenticatedPageLayout>
  ) : (
    <PageLayout
      className={styles.root}
      data-testid="SignInPage"
    >
      <div className={styles.layout}>
        <div className={styles.wrapper}>
          <AppLogo
            className={styles.logo}
            variant={APP_LOGO_VARIANT.black}
          />

          <UnauthenticatedPageContentLayout
            title={getTitle(isSSO, isInvited)}
            subtitle={getSubtitle(isSSO, isInvited)}
          >
            {isTeads ? (
              <TeadsLogin
                loading={loading}
                disabled={submitting}
                onStart={handleOAuthStart}
                onSuccess={handleOAuthSuccess}
                onError={handleOAuthError}
                error={error}
              />
            ) : (
              <>
                {!isSSO ? (
                  <>
                    <OAuthPanel
                      loading={loading}
                      disabled={submitting}
                      onStart={handleOAuthStart}
                      onSuccess={handleOAuthSuccess}
                      onError={handleOAuthError}
                    />

                    <AuthTypeSeparator />

                    <LoginForm
                      classes={{
                        root: styles.form,
                      }}
                      loading={loading || submitting}
                      error={error}
                      onSubmit={handleSubmit}
                    />

                    <UnauthenticatedPageFooterTextLink
                      text="Don’t have an account?"
                      link={{
                        to: ROUTES.SIGN_UP,
                        testId: 'SignInPageSignUpLink',
                        text: isInvited ? 'Register as a new user' : 'Sign up for free',
                      }}
                      from={location?.state?.from}
                    />
                  </>
                ) : (
                  <>
                    <SSOLogin
                      loading={loading}
                      disabled={submitting}
                      onStart={handleOAuthStart}
                      onSuccess={handleOAuthSuccess}
                      onError={handleOAuthError}
                      error={error}
                    />
                  </>
                )}
                <TextLink
                  className={styles.linkSwitchState}
                  variant="body2"
                  component="button"
                  onClick={() => setIsSSO((prevState) => !prevState)}
                >
                  {isSSO ? 'Return to log in' : 'Continue with SSO'}
                </TextLink>
              </>
            )}
          </UnauthenticatedPageContentLayout>
          <Link
            target="_blank"
            href={SUPPORT_URL}
            className={styles.contactUsLink}
          >
            Contact us
          </Link>
        </div>

        {!isMobile && <AuthenticationBanner className={styles.rightPanel} />}
      </div>
    </PageLayout>
  );
};
